import type {
  CarePathwayDto,
  CarePathwayStepDto,
  CarePathwayTemplateDto,
  CarePathwayTemplateStepDto,
} from '@/features/patient-record/api/carePathways';
import type {
  CarePathwayStatus,
  CarePathwayStepStatus,
  CarePathwayStepViewModel,
  CarePathwayTemplateState,
  CarePathwayTemplateStepViewModel,
  CarePathwayTemplateViewModel,
  CarePathwayViewModel,
} from '@/features/patient-record/types';
import { narrowEnum, nullableString } from '@/api/mappers/_shared';

const KNOWN_STATUS: ReadonlySet<CarePathwayStatus> = new Set<CarePathwayStatus>([
  'ACTIVE',
  'PAUSED',
  'COMPLETED',
  'CANCELLED',
]);

const KNOWN_STEP_STATUS: ReadonlySet<CarePathwayStepStatus> =
  new Set<CarePathwayStepStatus>(['NOT_STARTED', 'IN_PROGRESS', 'COMPLETED', 'SKIPPED']);

const KNOWN_TEMPLATE_STATE: ReadonlySet<CarePathwayTemplateState> =
  new Set<CarePathwayTemplateState>(['ACTIVE', 'DISABLED']);

function mapStep(dto: CarePathwayStepDto): CarePathwayStepViewModel {
  return {
    id: dto.id,
    name: dto.name,
    description: dto.description,
    status: narrowEnum<CarePathwayStepStatus>(
      dto.status,
      KNOWN_STEP_STATUS,
      'NOT_STARTED',
    ),
    sortOrder: typeof dto.sort_order === 'number' ? dto.sort_order : 0,
    completionNote: nullableString(dto.completion_note),
    skipRationale: nullableString(dto.skip_rationale),
    completedAt: nullableString(dto.completed_at),
    completedBy: nullableString(dto.completed_by),
  };
}

export function mapCarePathway(dto: CarePathwayDto): CarePathwayViewModel {
  const rawSteps = Array.isArray(dto.steps) ? dto.steps : [];
  const steps = [...rawSteps]
    .sort((a, b) => a.sort_order - b.sort_order)
    .map(mapStep);
  const completed = steps.filter(
    (s) => s.status === 'COMPLETED' || s.status === 'SKIPPED',
  ).length;
  return {
    id: dto.id,
    name: dto.name,
    description: dto.description,
    status: narrowEnum<CarePathwayStatus>(dto.status, KNOWN_STATUS, 'ACTIVE'),
    templateId: dto.template_id,
    completionReason: nullableString(dto.completion_reason),
    enrolledAt: nullableString(dto.enrolled_at),
    enrolledBy: nullableString(dto.enrolled_by),
    closedAt: nullableString(dto.closed_at),
    closedBy: nullableString(dto.closed_by),
    stepsCompleted: completed,
    stepsTotal: steps.length,
    steps,
  };
}

export function mapCarePathways(
  dtos: readonly CarePathwayDto[],
): CarePathwayViewModel[] {
  return dtos.map(mapCarePathway);
}

function mapTemplateStep(
  dto: CarePathwayTemplateStepDto,
): CarePathwayTemplateStepViewModel {
  return {
    id: dto.id,
    name: dto.name,
    description: nullableString(dto.description),
    sortOrder: typeof dto.sort_order === 'number' ? dto.sort_order : 0,
  };
}

export function mapCarePathwayTemplate(
  dto: CarePathwayTemplateDto,
): CarePathwayTemplateViewModel {
  // List endpoints sometimes omit `steps` (only the single-template GET
  // returns the full step list); guard so the mapper survives either shape.
  const rawSteps = Array.isArray(dto.steps) ? dto.steps : [];
  const steps = [...rawSteps]
    .sort((a, b) => a.sort_order - b.sort_order)
    .map(mapTemplateStep);
  // Live BE has returned both `step_count` / `step_counts` and
  // `enrollment_count` / `enrollment_counts` across deployments; read either
  // spelling without forcing FE callers to track which one is live today.
  const extras = dto as CarePathwayTemplateDto & {
    readonly step_counts?: number;
    readonly enrollment_counts?: number;
  };
  const resolvedStepCount =
    typeof dto.step_count === 'number'
      ? dto.step_count
      : typeof extras.step_counts === 'number'
        ? extras.step_counts
        : steps.length;
  const resolvedEnrollmentCount =
    typeof dto.enrollment_count === 'number'
      ? dto.enrollment_count
      : typeof extras.enrollment_counts === 'number'
        ? extras.enrollment_counts
        : 0;
  return {
    id: dto.id,
    name: dto.name,
    description: dto.description,
    state: narrowEnum<CarePathwayTemplateState>(
      dto.state,
      KNOWN_TEMPLATE_STATE,
      'ACTIVE',
    ),
    stepCount: resolvedStepCount,
    enrollmentCount: resolvedEnrollmentCount,
    steps,
    createdAt: nullableString(dto.created_at),
    modifiedAt: nullableString(dto.modified_at),
  };
}

export function mapCarePathwayTemplates(
  dtos: readonly CarePathwayTemplateDto[],
): CarePathwayTemplateViewModel[] {
  return dtos.map(mapCarePathwayTemplate);
}
